import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { Search, Add } from '@mui/icons-material';
import {
  Button,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box
} from '@mui/material';
import SalesExecutiveModal from './SalesExecutiveModal';
import ExecutiveCommissionSlabModal from './ExecutiveCommissionSlabModal';
import Pagination from '../../common/Pagination';

const SalesExecutiveIndex = () => {
  useSessionValidation();
  const { companyId, branchId, token, userId } = useAuth();
  const { logAudit } = useAuditLog();

  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const [salesExecutives, setSalesExecutives] = useState([]);
  const [balanceSheetHeads, setBalanceSheetHeads] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productItems, setProductItems] = useState([]);

  // Commission slab modal states
  const [commissionSlabOpen, setCommissionSlabOpen] = useState(false);
  const [commissionSlabExecutive, setCommissionSlabExecutive] = useState(null);
  const [viewSlabs, setViewSlabs] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({});

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [loadingVariant, setLoadingVariant] = useState('default');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const apiEndpoint = 'SalesExecutiveApi';
  const balanceSheetApiEndpoint = 'BalanceSheetHeadApi';
 
  useEffect(() => {
    if (companyId && token) {
      fetchAllData();
    }
  }, [companyId, token]);

  const fetchSalesExecutives = async () => {
    const response = await fetchData(apiEndpoint, `/${companyId}`, token);
    const data = response.response_data || response || [];
    const uniqueData = data.filter((item, index, self) =>
      index === self.findIndex((t) => t.Id === item.Id)
    );
    setSalesExecutives(uniqueData);
  };

  const fetchBalanceSheetHeads = async () => {
    try {
      const response = await fetchData(balanceSheetApiEndpoint, `/${companyId}`, token);
      setBalanceSheetHeads(response.response_data || response || []);
    } catch (err) {
      console.error('Error fetching balance sheet heads:', err);
    }
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Fetching sales executives...');
      setLoadingVariant('default');
      await Promise.allSettled([
        fetchSalesExecutives(),
        fetchBalanceSheetHeads(),
        fetchCategories(),
        fetchProductItems(),
      ]);
      setLoadingMessage('Data loaded successfully!');
      setLoadingVariant('success');
      setTimeout(() => setLoading(false), 800);
    } catch (err) {
      console.error('Error fetching data:', err);
      setLoadingMessage('Failed to load data!');
      setLoadingVariant('error');
      setError('Failed to fetch data. Please try again.');
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetchData('CategoryApi', `/${companyId}`, token);
      setCategories(res?.response_data || res || []);
    } catch { setCategories([]); }
  };

  const fetchProductItems = async () => {
    try {
      const res = await fetchData('ItemApi', `/${companyId}`, token);
      setProductItems(res?.response_data || res || []);
    } catch { setProductItems([]); }
  };

  const fetchSingleItem = async (id) => {
    try {
      const response = await fetchData(apiEndpoint, `/GetById/${id}`, token);
      return response.response_data || response || null;
    } catch (err) {
      console.error('Error fetching sales executive by ID:', err);
      return null;
    }
  };

  const handleOpenModal = async (action, item = null) => {
    setModalAction(action);
    if ((action === 'view' || action === 'edit') && item?.Id) {
      setLoading(true);
      setLoadingMessage('Fetching details...');
      setLoadingVariant('default');
      const freshData = await fetchSingleItem(item.Id);
      if (freshData) {
        const master = freshData?.Master || freshData || {};
        const typeObj = freshData?.SalesExecutive || freshData?.Capital || freshData?.Asset || freshData?.Others || freshData?.IncomeExpense || {};
        const openingBalances = freshData?.OpeningBalances || [];
        const unwrapped = {
          ...master,
          ...typeObj,
          OpeningBalances: openingBalances,
          MasterId: master.Id || 0,
          TypeId: typeObj.Id || 0,
        };
        setSelectedItem({ ...unwrapped });
        setFormData({ ...unwrapped });
      } else {
        setSelectedItem({ ...item });
        setFormData({ ...item });
      }
      if (action === 'view') {
        try {
          const res = await fetchData('SalesExecutiveApi', `/ItemWiseSlab/${companyId}/${item.Id}`, token);
          setViewSlabs(res?.responseCode === '1111' ? res.response_data : []);
        } catch { setViewSlabs([]); }
      }
      setLoading(false);
    } else {
      setSelectedItem(null);
      setFormData({});
    }
    setOpenModal(true);
  };

  const handleCloseModal = (skipReload = false) => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} sales executive...`);
      setLoadingVariant('default');

      let dataToSend = { ...formData, CompanyId: companyId };
      if (dataToSend.BalanceSheetHeadId) dataToSend.BalanceSheetHeadId = parseInt(dataToSend.BalanceSheetHeadId) || null;
      dataToSend.IsPayableOrReceivable = dataToSend.IsPayableOrReceivable ?? false;
      dataToSend.IsActive = dataToSend.IsActive ?? true;
      dataToSend.IsLoginNeeded = dataToSend.IsLoginNeeded === true;

      // ── Duplicate guard ─────────────────────────────────────────────────
      // Block only when both Name AND BalanceSheetHeadId are the same.
      // Same name with a different Balance Sheet Head is a valid distinct record.
      const currentName = (dataToSend.Name || '').trim().toLowerCase();
      const currentBSH  = dataToSend.BalanceSheetHeadId;
      const currentId   = formData?.MasterId || selectedItem?.MasterId || formData?.Id || 0;
      const duplicate = salesExecutives.some(exec => {
        const execId = exec.MasterId || exec.Id || 0;
        if (modalAction === 'edit' && execId === currentId) return false;
        const sameName = (exec.Name || '').trim().toLowerCase() === currentName;
        const sameBSH  = String(exec.BalanceSheetHeadId) === String(currentBSH);
        return sameName && sameBSH;
      });
      if (duplicate) {
        setError(`Duplicate Entry: A sales executive named "${dataToSend.Name}" with the same Balance Sheet Head already exists.`);
        setLoading(false);
        return;
      }
      // ────────────────────────────────────────────────────────────────────

      if (modalAction === 'create') {
        const isLoginNeeded = dataToSend.IsLoginNeeded === true;
        const vmPayload = {
          Master: {
            Id: 0,
            Name: dataToSend.Name,
            BalanceSheetHeadId: dataToSend.BalanceSheetHeadId,
            SalesTargetAmount: parseFloat(dataToSend.SalesTargetAmount) || 0,
            CompanyId: companyId,
            BranchId: branchId,
            IsLoginNeeded: isLoginNeeded,
            CreatedBy: userId,
            CreatedDate: new Date(),
          },
          SalesExecutive: {
            Id: 0,
            IsPayableOrReceivable: dataToSend.IsPayableOrReceivable,
            IsLoginNeeded: isLoginNeeded,
            BranchId: dataToSend.BranchId || null,
            IsActive: dataToSend.IsActive,            
            CreatedBy: userId,
            CreatedDate: new Date(),
          },
          IsLoginNeeded: isLoginNeeded,
          OpeningBalances: dataToSend.OpeningBalances || [],
        };
        const res = await postData(apiEndpoint, '', vmPayload, token);
        setSuccess('Sales executive created successfully!');
        logAudit(AUDIT_ACTION.CREATE, apiEndpoint, res?.response_data?.Id || res?.Id || 0, null, { Name: formData.Name || '' });
        const newExec = res?.response_data || res;
        handleCloseModal(true);
        await fetchSalesExecutives();
        if (newExec?.Id) {
          setCommissionSlabExecutive(newExec);
          setCommissionSlabOpen(true);
        }
      } else if (modalAction === 'edit') {
        const itemId = formData?.TypeId || formData?.Id || selectedItem?.TypeId || selectedItem?.Id;
        const masterId = formData?.MasterId || selectedItem?.MasterId || 0;
        if (!itemId) throw new Error('Item ID is missing');

        const vmPayload = {
          Master: {
            Id: masterId,
            Name: dataToSend.Name,
            BalanceSheetHeadId: dataToSend.BalanceSheetHeadId,
            CompanyId: companyId,
            BranchId: branchId,
            IsLoginNeeded: dataToSend.IsLoginNeeded === true,
            SalesTargetAmount: parseFloat(dataToSend.SalesTargetAmount) || 0,
            ModifiedBy: userId,
            ModifiedDate: new Date(),
            CreatedBy: dataToSend.CreatedBy,
            CreatedDate: dataToSend.CreatedDate,
          },
          SalesExecutive: {
            Id: itemId,
            IsPayableOrReceivable: dataToSend.IsPayableOrReceivable,
            IsLoginNeeded: dataToSend.IsLoginNeeded === true,
            BranchId: dataToSend.BranchId || null,
            IsActive: dataToSend.IsActive,            
            ModifiedBy: userId,
            ModifiedDate: new Date(),
            CreatedBy: dataToSend.CreatedBy,
            CreatedDate: dataToSend.CreatedDate,
          },
          IsLoginNeeded: dataToSend.IsLoginNeeded === true,
          OpeningBalances: dataToSend.OpeningBalances || [],
        };
        await updateData(apiEndpoint, itemId, vmPayload, token);
        setSuccess('Sales executive updated successfully!');
        logAudit(AUDIT_ACTION.UPDATE, apiEndpoint, itemId, { ...selectedItem }, { ...formData });
        handleCloseModal();
        await fetchSalesExecutives();
      }

      setLoading(false);
    } catch (err) {
      console.error('Error saving:', err);
      setError('Failed to save sales executive. Please try again.');
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const handleDeleteClick = (item) => {
    setDeleteItem(item);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      await deleteData(apiEndpoint, deleteItem.Id, token);
      setSalesExecutives(prev => prev.filter(i => i.Id !== deleteItem.Id));
      setSuccess('Sales executive deleted successfully!');
      logAudit(AUDIT_ACTION.DELETE, apiEndpoint, deleteItem.Id, { Name: deleteItem.Name }, null);
      setDeleteConfirmOpen(false);
      setDeleteItem(null);
      setTimeout(() => setLoading(false), 800);
    } catch (err) {
      console.error('Error deleting:', err);
      setError('Failed to delete sales executive. Please try again.');
      setDeleteConfirmOpen(false);
      setDeleteItem(null);
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDeleteItem(null);
  };

  const getFilteredData = () => {
    if (!searchTerm) return salesExecutives;
    return salesExecutives.filter(item =>
      item.Name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getPaginatedData = () => {
    const filtered = getFilteredData();
    const startIndex = (currentPage - 1) * rowsPerPage;
    return {
      data: filtered.slice(startIndex, startIndex + rowsPerPage),
      totalCount: filtered.length,
      totalPages: Math.ceil(filtered.length / rowsPerPage),
      startIndex
    };
  };

  const handleCloseSnackbar = () => { setError(''); setSuccess(''); };

  const { data: paginatedData, totalCount, totalPages, startIndex } = getPaginatedData();

  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', position: 'relative', color: 'black', fontFamily: 'Times New Romans' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Romans', textTransform: 'uppercase', color: 'black' }}>
            Sales Executives
          </h4>
        </div>

        {/* Search and Create */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: '#667eea', fontSize: '20px' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '29px',
                '&:hover fieldset': { borderColor: '#667eea' },
                '&.Mui-focused fieldset': { borderColor: '#667eea' },
              },
            }}
          />
          <Tooltip title="Create a new sales executive" arrow>
            <Button
              variant="contained"
              onClick={() => handleOpenModal('create')}
              sx={{
                background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)',
                color: 'white', padding: { xs: '6px', sm: '4px 12px' }, borderRadius: '6px',
                fontSize: '12px', fontWeight: '700', boxShadow: '0 2px 8px rgba(102,126,234,0.4)',
                minHeight: '29px', minWidth: { xs: '29px', sm: 'auto' }, textTransform: 'none',
                letterSpacing: '0.5px', whiteSpace: 'nowrap',
                '& .button-text': { display: { xs: 'none', sm: 'inline' } }
              }}
            >
              <Add sx={{ fontSize: '16px' }} />
              <span className="button-text" style={{ marginLeft: '4px' }}>Create Executive</span>
            </Button>
          </Tooltip>
        </div>

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow style={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', height: '27px' }}>
                  {['Sl No.', 'Name', 'Balance Sheet Head', 'Sales Target (₹)', 'Status', 'Actions'].map((col) => (
                    <TableCell key={col} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px', textAlign: col === 'Actions' ? 'right' : 'left' }}>
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item, index) => (
                  <TableRow key={item.Id} style={{ background: index % 2 === 0 ? '#ffffff' : '#f8f9fa', borderBottom: '2px solid #f0f0f0' }}>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{startIndex + index + 1}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.Name || '-'}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{balanceSheetHeads.find(bs => bs.Id === item.BalanceSheetHeadId)?.Name || '-'}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                      {item.SalesTargetAmount != null && item.SalesTargetAmount !== 0
                        ? `₹${Number(item.SalesTargetAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                        : '-'}
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                      <Chip label={item.IsActive ? 'Active' : 'Inactive'} color={item.IsActive ? 'success' : 'default'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                    </TableCell>
                    <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                        
                        <Tooltip title="View Details" arrow>
                          <Button size="small" onClick={() => handleOpenModal('view', item)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>👁️</Button>
                        </Tooltip>
                        <Tooltip title="Edit Sales Executive" arrow>
                          <Button size="small" onClick={() => handleOpenModal('edit', item)} disabled={!item.IsActive} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>✏️</Button>
                        </Tooltip>
                        <Tooltip title="Delete Sales Executive" arrow>
                          <Button size="small" onClick={() => handleDeleteClick(item)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>🗑️</Button>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {paginatedData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} style={{ textAlign: 'center', padding: '20px', color: '#718096', fontSize: '13px' }}>
                      No sales executives found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
          itemsPerPage={rowsPerPage}
          onItemsPerPageChange={(value) => { setRowsPerPage(Number(value)); setCurrentPage(1); }}
          totalItems={totalCount}
        />
      </div>

      <LogoLoader open={loading} message={loadingMessage} variant={loadingVariant} />

      <SalesExecutiveModal
        open={openModal}
        onClose={handleCloseModal}
        modalAction={modalAction}
        selectedItem={selectedItem}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSave}
        salesExecutives={salesExecutives}
        balanceSheetHeads={balanceSheetHeads}
        loading={loading}
        viewSlabs={viewSlabs}
        categories={categories}
        items={productItems}
      />

      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
            <Typography variant="h6" component="span" sx={{ fontWeight: 600, fontSize: '16px' }}>Confirm Delete</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: '#4a5568' }}>Are you sure you want to delete "{deleteItem?.Name}"?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} sx={{ textTransform: 'none', color: '#718096' }}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" sx={{ textTransform: 'none', borderRadius: '6px' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error || !!success} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={error ? 'error' : 'success'}>{error || success}</Alert>
      </Snackbar>

      {/* Executive Commission Slab Modal */}
      <ExecutiveCommissionSlabModal
        open={commissionSlabOpen}
        onClose={() => { setCommissionSlabOpen(false); setCommissionSlabExecutive(null); }}
        onSave={() => {}}
        item={commissionSlabExecutive}
        categories={categories}
        items={productItems}
        masters={salesExecutives}
      />
    </div>
  );
};

export default SalesExecutiveIndex;

